import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, Phone, Youtube } from "lucide-react";
import { PerlaLogo } from "./PerlaLogo";
import { useLang } from "@/contexts/LanguageContext";
import { s } from "@/i18n/translations";

// TikTok icon (inline SVG - no icon in lucide)
const TikTokIcon = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.68a8.16 8.16 0 0 0 4.77 1.52V6.79a4.85 4.85 0 0 1-1.84-.1Z"/>
  </svg>
);

// WhatsApp icon (inline SVG)
const WhatsAppIcon = ({ size = 16 }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0 0 20.464 3.488"/>
  </svg>
);

export const Footer = () => {
  const { lang, t } = useLang();
  const year = new Date().getFullYear();
  const phone = "+39 320 617 9277";
  const phoneRaw = "393206179277";

  return (
    <footer
      className="theme-dark border-t-4 border-[#FF5A1F] mt-24"
      data-testid="site-footer"
    >
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-16 grid gap-12 md:grid-cols-4">
        <div className="md:col-span-2">
          <PerlaLogo light />
          <p className="mt-6 text-[#FAFAF7]/70 max-w-md font-body">
            {t("footer.tagline", "Music management italiano per artisti che non si accontentano.")}
          </p>
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <a
              href="https://instagram.com/perlamusicmgmt"
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 border border-[#FAFAF7]/30 flex items-center justify-center hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors"
              data-testid="footer-instagram"
              aria-label="Instagram"
            >
              <Instagram size={18} />
            </a>
            <a
              href="https://www.tiktok.com/@perla.music.mgmt"
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 border border-[#FAFAF7]/30 flex items-center justify-center hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors"
              data-testid="footer-tiktok"
              aria-label="TikTok"
            >
              <TikTokIcon size={16} />
            </a>
            <a
              href="https://www.youtube.com/@PerlaMusic.Management"
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 border border-[#FAFAF7]/30 flex items-center justify-center hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors"
              data-testid="footer-youtube"
              aria-label="YouTube"
            >
              <Youtube size={18} />
            </a>
            <a
              href={`https://wa.me/${phoneRaw}`}
              target="_blank"
              rel="noreferrer"
              className="w-10 h-10 border border-[#FAFAF7]/30 flex items-center justify-center hover:border-[#FF5A1F] hover:text-[#FF5A1F] transition-colors"
              data-testid="footer-whatsapp"
              aria-label="WhatsApp"
            >
              <WhatsAppIcon size={16} />
            </a>
          </div>
        </div>

        <div>
          <h4 className="font-display text-xl tracking-wide text-[#FAFAF7] mb-4">
            {s("nav.contacts", lang).toUpperCase()}
          </h4>
          <ul className="space-y-3 text-sm text-[#FAFAF7]/70">
            <li className="flex items-center gap-2">
              <Mail size={14} className="text-[#FF5A1F]" />
              <a href="mailto:info.perlamgmt@gmail.com" className="hover:text-[#FF5A1F]" data-testid="footer-email">
                info.perlamgmt@gmail.com
              </a>
            </li>
            <li className="flex items-center gap-2">
              <Phone size={14} className="text-[#FF5A1F]" />
              <a href={`tel:${phoneRaw}`} className="hover:text-[#FF5A1F]" data-testid="footer-phone">
                {phone}
              </a>
            </li>
            <li className="flex items-center gap-2">
              <WhatsAppIcon size={14} />
              <a href={`https://wa.me/${phoneRaw}`} target="_blank" rel="noreferrer" className="hover:text-[#FF5A1F]" data-testid="footer-whatsapp-link">
                WhatsApp
              </a>
            </li>
          </ul>
        </div>

        <div>
          <h4 className="font-display text-xl tracking-wide text-[#FAFAF7] mb-4">MENU</h4>
          <ul className="space-y-2 text-sm text-[#FAFAF7]/70">
            <li><Link to="/" className="hover:text-[#FF5A1F]" data-testid="footer-link-home">{s("nav.home", lang)}</Link></li>
            <li><Link to="/chi-siamo" className="hover:text-[#FF5A1F]" data-testid="footer-link-about">{s("nav.about", lang)}</Link></li>
            <li><Link to="/servizi" className="hover:text-[#FF5A1F]" data-testid="footer-link-services">{s("nav.services", lang)}</Link></li>
            <li><Link to="/testimonianze" className="hover:text-[#FF5A1F]" data-testid="footer-link-testimonials">{s("nav.testimonials", lang)}</Link></li>
            <li><Link to="/contatti" className="hover:text-[#FF5A1F]" data-testid="footer-link-contacts">{s("nav.contacts", lang)}</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[#FAFAF7]/10">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-[#FAFAF7]/50">
          <span>© {year} Perla Music. {t("footer.rights", "Tutti i diritti riservati.")}</span>
          <div className="flex items-center gap-4">
            <Link to="/privacy" className="hover:text-[#FF5A1F]" data-testid="footer-privacy-link">Privacy Policy</Link>
            <Link to="/cookie-policy" className="hover:text-[#FF5A1F]" data-testid="footer-cookie-link">Cookie Policy</Link>
            <Link to="/admin/login" className="hover:text-[#FF5A1F]" data-testid="footer-admin-link">CMS</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
